/**
 * Kauf- und Renditerechner — reiner Rechenkern (kein I/O, CLAUDE.md Regel 5).
 *
 * Beurteilt einen Immobilienkauf aus Vermietersicht: Bruttomietrendite,
 * Nettomietrendite (auf die Gesamtinvestition inkl. Kaufnebenkosten), Kapitaldienst
 * (Zins + Tilgung), operativer Cashflow und Eigenkapitalrendite (Hebeleffekt).
 *
 * Keine Steuer, keine AfA — bewusst vor-steuerlich, damit die Zahl nachvollziehbar bleibt.
 * Beträge sind Integer in Cent (Regel 6); Prozentsätze sind Fließkommazahlen.
 */

import { assertCent, type Cent } from './geld';

export interface RenditeEingabe {
  kaufpreisCent: Cent;
  /** Kaufnebenkosten als Prozentsatz vom Kaufpreis. */
  grunderwerbsteuerProzent: number; // z. B. 5,0 (BW)
  notarProzent: number; // Notar + Grundbuch, i. d. R. ~1,5
  maklerProzent: number; // Käuferanteil, z. B. 3,57
  eigenkapitalCent: Cent;
  sollzinsProzent: number; // p. a.
  tilgungProzent: number; // anfänglich p. a.
  jahresKaltmieteCent: Cent;
  /** Nicht umlagefähige Bewirtschaftung p. a. (Verwaltung, Instandhaltung, Mietausfallwagnis). */
  bewirtschaftungskostenCent: Cent;
}

export interface RenditeErgebnis {
  kaufnebenkostenCent: Cent;
  gesamtinvestitionCent: Cent;
  darlehenCent: Cent;
  /** Jahreskaltmiete − Bewirtschaftung. */
  jahresreinertragCent: Cent;
  bruttomietrenditeProzent: number; // Jahreskaltmiete / Kaufpreis
  nettomietrenditeProzent: number; // Reinertrag / Gesamtinvestition
  jahreszinsCent: Cent;
  jahrestilgungCent: Cent;
  kapitaldienstCent: Cent; // Annuität = Zins + Tilgung
  /** Reinertrag − Kapitaldienst (nach Tilgung), p. a. */
  cashflowVorSteuerCent: Cent;
  cashflowMonatlichCent: Cent;
  /** (Reinertrag − Zins) / Eigenkapital — Eigenkapitalrendite vor Steuer. */
  eigenkapitalrenditeProzent: number;
  /** Kaufpreis als Vielfaches der Jahreskaltmiete. */
  kaufpreisfaktor: number;
}

function prozentAufCent(basis: Cent, prozent: number): Cent {
  return Math.round(basis * (prozent / 100));
}

function proz(zaehler: number, nenner: number): number {
  if (nenner <= 0) return 0;
  return Math.round((zaehler / nenner) * 10000) / 100; // zwei Nachkommastellen
}

export function berechneRendite(e: RenditeEingabe): RenditeErgebnis {
  assertCent(e.kaufpreisCent, 'Kaufpreis');
  assertCent(e.eigenkapitalCent, 'Eigenkapital');
  assertCent(e.jahresKaltmieteCent, 'Jahreskaltmiete');
  assertCent(e.bewirtschaftungskostenCent, 'Bewirtschaftungskosten');
  if (e.kaufpreisCent <= 0) throw new Error('Kaufpreis muss größer als 0 sein');

  const kaufnebenkosten =
    prozentAufCent(e.kaufpreisCent, e.grunderwerbsteuerProzent) +
    prozentAufCent(e.kaufpreisCent, e.notarProzent) +
    prozentAufCent(e.kaufpreisCent, e.maklerProzent);

  const gesamtinvestition = e.kaufpreisCent + kaufnebenkosten;
  const darlehen = Math.max(0, gesamtinvestition - e.eigenkapitalCent);

  const jahresreinertrag = e.jahresKaltmieteCent - e.bewirtschaftungskostenCent;

  const jahreszins = prozentAufCent(darlehen, e.sollzinsProzent);
  const jahrestilgung = prozentAufCent(darlehen, e.tilgungProzent);
  const kapitaldienst = jahreszins + jahrestilgung;

  const cashflow = jahresreinertrag - kapitaldienst;

  return {
    kaufnebenkostenCent: kaufnebenkosten,
    gesamtinvestitionCent: gesamtinvestition,
    darlehenCent: darlehen,
    jahresreinertragCent: jahresreinertrag,
    bruttomietrenditeProzent: proz(e.jahresKaltmieteCent, e.kaufpreisCent),
    nettomietrenditeProzent: proz(jahresreinertrag, gesamtinvestition),
    jahreszinsCent: jahreszins,
    jahrestilgungCent: jahrestilgung,
    kapitaldienstCent: kapitaldienst,
    cashflowVorSteuerCent: cashflow,
    cashflowMonatlichCent: Math.round(cashflow / 12),
    eigenkapitalrenditeProzent:
      e.eigenkapitalCent > 0 ? proz(jahresreinertrag - jahreszins, e.eigenkapitalCent) : 0,
    kaufpreisfaktor:
      e.jahresKaltmieteCent > 0
        ? Math.round((e.kaufpreisCent / e.jahresKaltmieteCent) * 10) / 10
        : 0,
  };
}
